import React, { useContext, useEffect, useState } from 'react';
import { DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import {
  Button,
  Card,
  Divider,
  Drawer,
  Form,
  Input,
  InputNumber,
  Select,
  Space,
  Switch,
} from 'antd';

import EventConfigurationContext from '@totem/components/services/threats/configuration/EventConfigurationContext';
import {
  EMPTY_HIERARCHY,
  EMPTY_OBJECT_ID,
  EventConfigurationItem,
  HierarchyReference,
  IMPACT_URGENCY_OPTIONS,
  ObjectReference,
  STATUS_OPTIONS,
} from '@totem/components/services/threats/configuration/types';
import OrgHierarchySelector from '@totem/components/ui_components/widgets/hierarchySelector/orgHierarchySelector';

const impactUrgencyOptions = IMPACT_URGENCY_OPTIONS.map((level) => ({
  label: level,
  value: level,
}));

const statusOptions = STATUS_OPTIONS.map((status) => ({
  label: status,
  value: status,
}));

const defaultIdOrUndefined = (ref: ObjectReference): string | undefined =>
  ref?.id && ref.id !== EMPTY_OBJECT_ID ? ref.id : undefined;

const EventConfigurationEditDrawer = () => {
  const {
    categories,
    editingItem,
    itemDrawerOpen,
    onItemDrawerClose,
    onSaveItem,
  } = useContext(EventConfigurationContext);
  const [form] = Form.useForm<EventConfigurationItem>();
  const [hierarchy, setHierarchy] =
    useState<HierarchyReference>(EMPTY_HIERARCHY);

  const isNew = !editingItem?.id;

  const categoryOptions = categories.map((category) => ({
    label: `${category.name} (${category.id})`,
    value: category.id,
  }));

  useEffect(() => {
    if (itemDrawerOpen && editingItem) {
      // Older records predate the status/hierarchy fields — default
      // defensively so a blank Status doesn't fail required-field
      // validation and silently block the whole save.
      form.setFieldsValue({
        ...editingItem,
        status: editingItem.status || 'Active',
        sequence: editingItem.sequence || 100,
        claimKeys: editingItem.claimKeys || [],
        cancelsKeys: editingItem.cancelsKeys || [],
      });

      setHierarchy(editingItem.hierarchy ?? EMPTY_HIERARCHY);
    } else if (!itemDrawerOpen) {
      form.resetFields();

      setHierarchy(EMPTY_HIERARCHY);
    }
  }, [itemDrawerOpen, editingItem, form]);

  const handleHierarchyChanged = (
    organization: ObjectReference,
    region: ObjectReference,
    building: ObjectReference,
    controlSystem: ObjectReference,
  ) => {
    setHierarchy({ organization, region, building, controlSystem });
  };

  const handleSave = () => {
    form.validateFields().then((values) => {
      onSaveItem({
        ...editingItem,
        ...values,
        hierarchy,
        technicianInfo: {
          likelyCauses: (values.technicianInfo?.likelyCauses ?? []).map(
            (cause, index) => ({ sequence: index + 1, message: cause.message }),
          ),
          actions: (values.technicianInfo?.actions ?? []).map(
            (action, index) => ({
              sequence: index + 1,
              message: action.message,
            }),
          ),
        },
      });
    });
  };

  return (
    <Drawer
      title={isNew ? 'Add Configuration Item' : 'Edit Configuration Item'}
      open={itemDrawerOpen}
      onClose={onItemDrawerClose}
      width={720}
      destroyOnClose
      extra={
        <Space>
          <Button onClick={onItemDrawerClose}>Cancel</Button>
          <Button type="primary" onClick={handleSave}>
            {isNew ? 'Create' : 'Save'}
          </Button>
        </Space>
      }
    >
      <Form form={form} layout="vertical" size="small">
        <Card size="small" title="Basic Info" style={{ marginBottom: 16 }}>
          <Form.Item
            name="category"
            label="Category"
            rules={[{ required: true, message: 'Required' }]}
          >
            <Select options={categoryOptions} placeholder="Select category" />
          </Form.Item>

          <Space style={{ width: '100%' }} size="middle">
            <Form.Item
              name="status"
              label="Status"
              rules={[{ required: true, message: 'Required' }]}
              style={{ flex: 1, minWidth: 0 }}
            >
              <Select options={statusOptions} />
            </Form.Item>
            <Form.Item
              name="sequence"
              label="Sequence"
              tooltip="Rules for the same trigger run in ascending sequence order. Existing EVENT rules conventionally use 100."
              rules={[{ required: true, message: 'Required' }]}
              style={{ flex: 1, minWidth: 0 }}
            >
              <InputNumber style={{ width: '100%' }} min={0} />
            </Form.Item>
          </Space>

          <Form.Item name="example" label="Example">
            <Input.TextArea rows={2} />
          </Form.Item>
        </Card>

        <Card
          size="small"
          title="Hierarchy"
          style={{ marginBottom: 16 }}
          extra={
            <span style={{ fontWeight: 'normal' }}>
              Optional — leave unset to apply globally
            </span>
          }
        >
          <OrgHierarchySelector
            key={editingItem?.id || 'new'}
            showOrganization
            showRegions={false}
            showBuildings
            showControlSystems
            allowUnassignedOrganization
            allowUnassignedBuilding
            showLabels
            defaultOrganizationId={
              hierarchy.organization?.id || EMPTY_OBJECT_ID
            }
            defaultBuildingId={defaultIdOrUndefined(hierarchy.building)}
            defaultControlSystemId={defaultIdOrUndefined(
              hierarchy.controlSystem,
            )}
            onChange={handleHierarchyChanged}
          />
        </Card>

        <Card
          size="small"
          title="Ticket Configuration"
          style={{ marginBottom: 16 }}
        >
          <Form.Item
            name={['ticketConfiguration', 'create']}
            label="Create Ticket"
            valuePropName="checked"
          >
            <Switch />
          </Form.Item>

          <Space style={{ width: '100%' }} size="middle">
            <Form.Item
              name={['ticketConfiguration', 'impact']}
              label="Impact"
              style={{ flex: 1, minWidth: 0 }}
            >
              <Select options={impactUrgencyOptions} />
            </Form.Item>
            <Form.Item
              name={['ticketConfiguration', 'urgency']}
              label="Urgency"
              style={{ flex: 1, minWidth: 0 }}
            >
              <Select options={impactUrgencyOptions} />
            </Form.Item>
          </Space>

          <Space style={{ width: '100%' }} size="middle">
            <Form.Item
              name={['ticketConfiguration', 'category']}
              label="Ticket Category"
              style={{ flex: 1, minWidth: 0 }}
            >
              <Input />
            </Form.Item>
            <Form.Item
              name={['ticketConfiguration', 'subCategory']}
              label="Ticket Sub-Category"
              style={{ flex: 1, minWidth: 0 }}
            >
              <Input />
            </Form.Item>
          </Space>

          <Form.Item
            name="claimKeys"
            label="Claim Keys"
            tooltip="When this item's ticket action fires, these keys are marked claimed. A later rule or configuration item whose own Claim Keys overlap is skipped."
          >
            <Select
              mode="tags"
              open={false}
              tokenSeparators={[',']}
              placeholder="Add key(s), e.g. backup-aging-notification"
            />
          </Form.Item>

          <Form.Item
            name="cancelsKeys"
            label="Cancels Keys"
            tooltip="When this item's ticket action fires, these keys are marked claimed in the rules engine — suppressing any other rule or configuration item whose Claim Keys match one of them."
          >
            <Select
              mode="tags"
              open={false}
              tokenSeparators={[',']}
              placeholder="Add key(s), e.g. backup-aging-notification"
            />
          </Form.Item>
        </Card>

        <Card size="small" title="Event Matches" style={{ marginBottom: 16 }}>
          <Form.List name="eventMatches">
            {(fields, { add, remove }) => (
              <>
                {fields.map((field) => (
                  <Space
                    key={field.key}
                    style={{ width: '100%', marginBottom: 8 }}
                    align="baseline"
                  >
                    <Form.Item
                      name={[field.name, 'sourceSystem']}
                      rules={[{ required: true, message: 'Required' }]}
                    >
                      <Input placeholder="sourceSystem" />
                    </Form.Item>
                    <Form.Item
                      name={[field.name, 'sourceType']}
                      rules={[{ required: true, message: 'Required' }]}
                    >
                      <Input placeholder="sourceType" />
                    </Form.Item>
                    <Form.Item
                      name={[field.name, 'eventType']}
                      rules={[{ required: true, message: 'Required' }]}
                    >
                      <Input placeholder="eventType" />
                    </Form.Item>
                    <Form.Item name={[field.name, 'descriptionContains']}>
                      <Input placeholder="descriptionContains (optional)" />
                    </Form.Item>
                    <Button
                      icon={<DeleteOutlined />}
                      danger
                      onClick={() => remove(field.name)}
                    />
                  </Space>
                ))}
                <Button
                  type="dashed"
                  block
                  icon={<PlusOutlined />}
                  onClick={() =>
                    add({
                      sourceSystem: '',
                      sourceType: '',
                      eventType: '',
                      descriptionContains: '',
                    })
                  }
                >
                  Add Event Match
                </Button>
              </>
            )}
          </Form.List>
        </Card>

        <Card size="small" title="Customer Info" style={{ marginBottom: 16 }}>
          <Form.Item
            name={['customerInfo', 'whatItMeans']}
            label="What It Means"
          >
            <Input.TextArea rows={2} />
          </Form.Item>
          <Form.Item
            name={['customerInfo', 'whyTicketRaised']}
            label="Why Ticket Raised"
          >
            <Input.TextArea rows={2} />
          </Form.Item>
          <Form.Item name={['customerInfo', 'impact']} label="Impact">
            <Input.TextArea rows={2} />
          </Form.Item>
        </Card>

        <Card size="small" title="Technician Info">
          <Divider titlePlacement="left" plain>
            Likely Causes
          </Divider>
          <Form.List name={['technicianInfo', 'likelyCauses']}>
            {(fields, { add, remove }) => (
              <>
                {fields.map((field) => (
                  <Space
                    key={field.key}
                    style={{ width: '100%', marginBottom: 8 }}
                    align="baseline"
                  >
                    <Form.Item
                      name={[field.name, 'message']}
                      style={{ width: 500 }}
                      rules={[{ required: true, message: 'Required' }]}
                    >
                      <Input placeholder="Likely cause" />
                    </Form.Item>
                    <Button
                      icon={<DeleteOutlined />}
                      danger
                      onClick={() => remove(field.name)}
                    />
                  </Space>
                ))}
                <Button
                  type="dashed"
                  block
                  icon={<PlusOutlined />}
                  onClick={() =>
                    add({ sequence: fields.length + 1, message: '' })
                  }
                >
                  Add Likely Cause
                </Button>
              </>
            )}
          </Form.List>

          <Divider titlePlacement="left" plain>
            Actions
          </Divider>
          <Form.List name={['technicianInfo', 'actions']}>
            {(fields, { add, remove }) => (
              <>
                {fields.map((field) => (
                  <Space
                    key={field.key}
                    style={{ width: '100%', marginBottom: 8 }}
                    align="baseline"
                  >
                    <Form.Item
                      name={[field.name, 'message']}
                      style={{ width: 500 }}
                      rules={[{ required: true, message: 'Required' }]}
                    >
                      <Input placeholder="Action" />
                    </Form.Item>
                    <Button
                      icon={<DeleteOutlined />}
                      danger
                      onClick={() => remove(field.name)}
                    />
                  </Space>
                ))}
                <Button
                  type="dashed"
                  block
                  icon={<PlusOutlined />}
                  onClick={() =>
                    add({ sequence: fields.length + 1, message: '' })
                  }
                >
                  Add Action
                </Button>
              </>
            )}
          </Form.List>
        </Card>
      </Form>
    </Drawer>
  );
};

export default EventConfigurationEditDrawer;
